import { createContext, type ReactNode, useContext } from 'react';
import { useInvoiceParamsPresence, usePhieuChiExport } from '../hooks';
import { PhieuChi, PhieuChiInput } from '@/types/schemas';
import type { PhieuChiSearchFilters } from '../types';
import { QUERY_KEYS } from '@/constants';
import { useCRUD } from '@/hooks';

interface PhieuChiSearchContextValue {
  // CRUD operations
  data: PhieuChi[];
  addItem: (item: PhieuChiInput) => Promise<PhieuChi>;
  updateItem: (id: string, item: PhieuChiInput) => Promise<PhieuChi>;
  deleteItem: (id: string) => Promise<void>;
  refreshData: () => Promise<void>;
  fetchDetail: (id: string) => Promise<PhieuChi | PhieuChi[]>;
  detail: PhieuChi | null;
  isLoading: boolean;

  // Pagination
  currentPage: number;
  totalItems: number;
  handlePageChange: (page: number) => Promise<void>;
  sumTotal: number | null;

  // Search
  searchData: (filters: PhieuChiSearchFilters, page?: number, searchPageSize?: number) => Promise<void>;
  isFiltered: boolean;
  isSearching: boolean;

  // Export
  exportToExcel: () => Promise<void>;
  canExport: boolean;
  isExporting: boolean;
}

const PhieuChiSearchContext = createContext<PhieuChiSearchContextValue | null>(null);

export function usePhieuChiSearchContext() {
  const context = useContext(PhieuChiSearchContext);
  if (!context) {
    throw new Error('usePhieuChiSearchContext must be used within PhieuChiSearchProvider');
  }
  return context;
}

export function PhieuChiSearchProvider({ children }: { children: ReactNode }) {
  const hasInvoiceParams = useInvoiceParamsPresence();

  const {
    data,
    addItem,
    updateItem,
    deleteItem,
    refreshData,
    fetchDetail,
    detail,
    totalItems,
    currentPage,
    handlePageChange,
    isLoading,
    sumTotal,
    searchData,
    isFiltered,
    isSearching
  } = useCRUD<PhieuChi, PhieuChiInput>({
    endpoint: QUERY_KEYS.PHIEU_CHI,
    initialFetch: !hasInvoiceParams,
    sumField: 't_tien',
    useFilter: true
  });

  const { exportToExcel, canExport, isExporting } = usePhieuChiExport();

  return (
    <PhieuChiSearchContext.Provider
      value={{
        // CRUD
        data: data || [],
        addItem,
        updateItem,
        deleteItem,
        refreshData,
        fetchDetail,
        detail,
        isLoading,

        // Pagination
        currentPage,
        totalItems,
        handlePageChange,
        sumTotal,

        // Search
        searchData,
        isFiltered,
        isSearching,

        // Export
        exportToExcel,
        canExport,
        isExporting
      }}
    >
      {children}
    </PhieuChiSearchContext.Provider>
  );
}
